import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { UserCircle, Mail, Building2, Shield, AlertTriangle, CreditCard } from 'lucide-react';
import { KYCForm } from '../components/KYCForm';
import { ProfessionalSignature } from '../components/ProfessionalSignature';
import { supabase } from '../services/supabaseClient';

const PageContainer = styled.div`
  padding: 2rem 2.5rem;
  max-width: 1560px;
  margin: 0 auto;
  min-height: 100vh;
  color: #f8fafc;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2.25rem;
  
  h1 {
    font-size: clamp(1.75rem, 3.5vw, 2.5rem);
    font-weight: 800;
    letter-spacing: -0.03em;
    background: linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.85rem;

    svg {
      color: #10b981; 
    }
  }
`;

const ProfileCard = styled.div`
  background: rgba(17, 24, 39, 0.7);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1.5rem;
  padding: 2.25rem;
  box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.35);
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.75rem;
  margin-bottom: 2.25rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const AvatarCircle = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.75rem;
  font-weight: 800;
  color: white;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
`;

const UserName = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.75rem;
  font-weight: 800;
  color: #f8fafc;
`;

const UserEmail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #94a3b8;
  font-size: 0.95rem;

  svg {
    color: #10b981;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.75rem;
`;

const InfoCard = styled.div`
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 1.25rem;
  padding: 1.75rem;

  h3 {
    margin: 0 0 1.25rem 0;
    font-size: 1.1rem;
    font-weight: 700;
    color: #f1f5f9;
    display: flex;
    align-items: center;
    gap: 0.6rem;

    svg {
      color: #10b981;
    }
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1.1rem;
  
  &:last-child {
    margin-bottom: 0;
  }

  .label {
    font-size: 0.75rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
  }

  .value {
    font-size: 1.05rem;
    color: #f8fafc;
    font-weight: 600;
  }
`;

const RoleBadge = styled.span<{ role: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.85rem;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  background: ${props => {
    switch (props.role?.toLowerCase()) {
      case 'owner': return 'rgba(var(--primary-color-rgb, 168, 85, 247), 0.15)'; // Purple
      case 'super_admin': return 'rgba(239, 68, 68, 0.15)'; // Red
      case 'admin': return 'rgba(59, 130, 246, 0.15)'; // Blue
      case 'grower': return 'rgba(34, 197, 94, 0.15)'; // Green
      case 'medico': return 'rgba(234, 179, 8, 0.15)'; // Yellow
      case 'partner': return 'rgba(34, 197, 94, 0.15)'; // Green
      default: return 'rgba(100, 116, 139, 0.15)'; // Slate
    }
  }};
  
  color: ${props => {
    switch (props.role?.toLowerCase()) {
      case 'owner': return '#c084fc';
      case 'super_admin': return '#f87171';
      case 'admin': return '#60a5fa';
      case 'grower': return '#4ade80';
      case 'medico': return '#facc15';
      case 'partner': return '#4ade80';
      default: return '#94a3b8';
    }
  }};
  
  border: 1px solid ${props => {
    switch (props.role?.toLowerCase()) {
      case 'owner': return 'rgba(var(--primary-color-rgb, 168, 85, 247), 0.3)';
      case 'super_admin': return 'rgba(239, 68, 68, 0.3)';
      case 'admin': return 'rgba(59, 130, 246, 0.3)';
      case 'grower': return 'rgba(34, 197, 94, 0.3)';
      case 'medico': return 'rgba(234, 179, 8, 0.3)';
      case 'partner': return 'rgba(34, 197, 94, 0.3)';
      default: return 'rgba(100, 116, 139, 0.3)';
    }
  }};
`;

const AccountInfo: React.FC = () => {
  const { user } = useAuth();
  const { currentOrganization, currentRole } = useOrganization();
  const [patientData, setPatientData] = useState<any>(null);

  useEffect(() => {
    async function loadPatient() {
      if (currentRole === 'partner' && user?.id) {
        const { data, error } = await supabase
          .from('aurora_patients')
          .select('*')
          .eq('profile_id', user.id)
          .single();
        if (data && !error) {
          setPatientData(data);
        }
      }
    }
    loadPatient();
  }, [currentRole, user?.id]);

  // Get initials for Avatar
  const getInitials = (name: string) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const roleDisplayNames: Record<string, string> = {
    'owner': 'Dueño',
    'admin': 'Administrador',
    'grower': 'Director de Cultivo',
    'medico': 'Director Médico',
    'staff': 'Operario',
    'super_admin': 'Super Administrador',
    'partner': 'Socio'
  };

  const displayRoleName = currentRole ? (roleDisplayNames[currentRole] || currentRole) : 'Usuario';

  return (
    <PageContainer>
      <Header>
        <h1><UserCircle size={32} style={{ color: '#10b981' }} /> Información de Cuenta</h1>
      </Header>

      <ProfileCard>
        <ProfileHeader>
          <AvatarCircle>
            {getInitials(user?.name || user?.email || '')}
          </AvatarCircle>
          <div>
            <UserName>{user?.name || 'Usuario'}</UserName>
            <UserEmail>
              <Mail size={14} /> {user?.email}
            </UserEmail>
          </div>
        </ProfileHeader>

        <InfoGrid>
          <InfoCard>
            <h3><Shield size={16} /> Permisos y Rol</h3>
            <InfoRow>
              <span className="label">Nivel de Acceso</span>
              <div style={{ marginTop: '0.25rem' }}>
                <RoleBadge role={currentRole || 'N/A'}>
                  {displayRoleName}
                </RoleBadge>
              </div>
            </InfoRow>
            <InfoRow>
              <span className="label">Identificador de Usuario (ID)</span>
              <span className="value" style={{ fontSize: '0.85rem', color: '#94a3b8', wordBreak: 'break-all' }}>
                {user?.id}
              </span>
            </InfoRow>
          </InfoCard>

          <InfoCard>
            <h3><Building2 size={16} /> Organización Actual</h3>
            {currentOrganization ? (
              <>
                <InfoRow>
                  <span className="label">Nombre</span>
                  <span className="value">{currentOrganization.name}</span>
                </InfoRow>
                <InfoRow>
                  <span className="label">Tipo de Plan</span>
                  <span className="value" style={{ textTransform: 'capitalize' }}>
                    {currentOrganization.plan || 'Free'}
                  </span>
                </InfoRow>
                <InfoRow>
                  <span className="label">Fecha de alta</span>
                  <span className="value">
                    {new Date(currentOrganization.created_at).toLocaleDateString('es-ES')}
                  </span>
                </InfoRow>
                <InfoRow>
                  <span className="label">Fecha de renovación</span>
                  <span className="value" style={{ color: '#4ade80' }}>
                    {(() => {
                      const d = new Date(currentOrganization.created_at);
                      d.setMonth(d.getMonth() + 1);
                      return d.toLocaleDateString('es-ES');
                    })()}
                  </span>
                </InfoRow>
                <InfoRow>
                  <span className="label">Organización ID</span>
                  <span className="value" style={{ fontSize: '0.85rem', color: '#94a3b8', wordBreak: 'break-all' }}>
                    {currentOrganization.id}
                  </span>
                </InfoRow>
              </>
            ) : (
              <div style={{ color: '#94a3b8', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertTriangle size={16} style={{ color: '#facc15' }} />
                No estás asociado a ninguna organización activa.
              </div>
            )}
          </InfoCard>
        </InfoGrid>

      </ProfileCard>

      {/* Show Signature Component for Medical Roles only*/}
      {currentRole === 'medico' && user && (
        <ProfessionalSignature
          userId={user.id}
          currentSignatureUrl={user.professional_signature_url}
          onSignatureUpdate={(url) => {
            // We trigger a hard reload of context or rely on local window refresh to ensure the App picks up the URL change.
            // Typically in TrazAPP we can force a page reload to avoid complex Context invalidation logic.
            window.location.reload();
          }}
        />
      )}

      {/* Show KYC Form only for Owners */}
      {currentRole === 'owner' && (
        <KYCForm />
      )}

      {/* Show Patient Data only for Partners */}
      {currentRole === 'partner' && patientData && (
        <ProfileCard style={{ marginTop: '2rem' }}>
          <h2 style={{ color: '#f8fafc', margin: '0 0 1.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.25rem' }}>
            <CreditCard size={20} style={{ color: '#4ade80' }} /> Datos del Paciente / Reprocann
          </h2>
          <InfoGrid>
            <InfoCard>
              <h3>Información Personal</h3>
              <InfoRow>
                <span className="label">DNI / Documento</span>
                <span className="value">{patientData.document_number || '-'}</span>
              </InfoRow>
              <InfoRow>
                <span className="label">Teléfono</span>
                <span className="value">{patientData.phone || '-'}</span>
              </InfoRow>
              <InfoRow>
                <span className="label">Dirección</span>
                <span className="value">
                  {[patientData.address, patientData.city, patientData.state].filter(Boolean).join(', ') || '-'}
                </span>
              </InfoRow>
            </InfoCard>

            <InfoCard>
              <h3>Estado Legal / Médico</h3>
              <InfoRow>
                <span className="label">Límite Mensual Asignado</span>
                <span className="value" style={{ color: '#4ade80', fontWeight: 'bold' }}>{patientData.monthly_limit_grams}g</span>
              </InfoRow>
              <InfoRow>
                <span className="label">Vencimiento Reprocann</span>
                <span className="value" style={{ color: patientData.reprocann_expiration ? '#f8fafc' : '#facc15' }}>
                  {patientData.reprocann_expiration ? new Date(patientData.reprocann_expiration).toLocaleDateString('es-ES') : 'Sin Reprocann'}
                </span>
              </InfoRow>
              <InfoRow>
                <span className="label">Documentación Subida</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '0.25rem' }}>
                  {patientData.reprocann_url && <a href={patientData.reprocann_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', fontSize: '0.9rem', textDecoration: 'none' }}>Ver Reprocann</a>}
                  {patientData.front_id_url && <a href={patientData.front_id_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', fontSize: '0.9rem', textDecoration: 'none' }}>Frente DNI</a>}
                  {patientData.back_id_url && <a href={patientData.back_id_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', fontSize: '0.9rem', textDecoration: 'none' }}>Dorso DNI</a>}
                  {!patientData.reprocann_url && !patientData.front_id_url && !patientData.back_id_url && <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Sin documentos</span>}
                </div>
              </InfoRow>
            </InfoCard>
          </InfoGrid>
        </ProfileCard>
      )}

    </PageContainer>
  );
};

export default AccountInfo;
